// Data generators for creating realistic mock data
import { 
  SalesRep, Product, Account, Contact, Lead, Opportunity, Activity,
  ICPTier, LeadSource, LeadStatus, DealStage, ProductTier, Industry, CompanySize, Region,
  StageHistoryEntry
} from './types';

// Utility functions
const randomId = () => Math.random().toString(36).substring(2, 11);
const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomFloat = (min: number, max: number) => Math.random() * (max - min) + min;
const randomItem = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const randomDate = (start: Date, end: Date) => new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));

// Name generators
const firstNames = ['James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Christopher', 'Karen', 'Alex', 'Emma', 'Daniel', 'Olivia', 'Matthew', 'Sophia', 'Anthony', 'Isabella', 'Mark', 'Mia', 'Steven', 'Charlotte', 'Andrew', 'Amelia', 'Joshua', 'Harper', 'Kenneth', 'Evelyn', 'Kevin', 'Abigail'];
const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson'];
const companyPrefixes = ['Acme', 'Global', 'Tech', 'Digital', 'Smart', 'Cloud', 'Data', 'Cyber', 'Net', 'Bio', 'Green', 'Blue', 'Prime', 'Core', 'Nova', 'Quantum', 'Alpha', 'Beta', 'Omega', 'Apex'];
const companySuffixes = ['Corp', 'Inc', 'Systems', 'Solutions', 'Technologies', 'Labs', 'Group', 'Industries', 'Enterprises', 'Partners', 'Dynamics', 'Works', 'Logic', 'Soft', 'Tech'];

const generateName = () => `${randomItem(firstNames)} ${randomItem(lastNames)}`;
const generateCompanyName = () => `${randomItem(companyPrefixes)} ${randomItem(companySuffixes)}`;
const generateEmail = (name: string, domain: string) => `${name.toLowerCase().replace(' ', '.')}@${domain}`;

// Generate sales reps
export function generateSalesReps(count: number = 10): SalesRep[] {
  const teams = ['Enterprise Sales', 'Mid-Market Sales'];
  const reps: SalesRep[] = [];
  
  for (let i = 0; i < count; i++) {
    const name = generateName();
    const team = i < count / 2 ? teams[0] : teams[1];
    const baseQuota = team === 'Enterprise Sales' ? 500000 : 250000;
    
    reps.push({
      id: `rep-${randomId()}`,
      name,
      email: generateEmail(name, 'revops-company.com'),
      team,
      quota: baseQuota + randomInt(-50000, 100000),
      hireDate: randomDate(new Date('2021-01-01'), new Date('2024-06-01')),
      isActive: true,
    });
  }
  
  return reps;
}

// Generate products
export function generateProducts(): Product[] {
  return [
    { id: 'prod-starter', name: 'Starter', tier: 'Starter', monthlyPrice: 49, annualPrice: 470, description: 'Perfect for small teams getting started' },
    { id: 'prod-growth', name: 'Growth', tier: 'Growth', monthlyPrice: 149, annualPrice: 1430, description: 'For growing teams needing more power' },
    { id: 'prod-pro', name: 'Professional', tier: 'Pro', monthlyPrice: 349, annualPrice: 3350, description: 'Advanced features for serious teams' },
    { id: 'prod-enterprise', name: 'Enterprise', tier: 'Enterprise', monthlyPrice: 799, annualPrice: 7670, description: 'Full suite with dedicated support' },
    { id: 'prod-custom', name: 'Custom', tier: 'Custom', monthlyPrice: 0, annualPrice: 0, description: 'Tailored solutions for unique needs' },
  ];
}

// Calculate ICP score and tier
function calculateICP(account: Partial<Account>): { score: number; tier: ICPTier } {
  let score = 0;
  
  // Industry scoring
  const industryScores: Record<Industry, number> = {
    'Technology': 25, 'Finance': 22, 'Healthcare': 20, 'Manufacturing': 15,
    'Retail': 12, 'Education': 10, 'Services': 18
  };
  score += industryScores[account.industry!] || 10;
  
  // Size scoring
  const sizeScores: Record<CompanySize, number> = { 'Enterprise': 30, 'Mid-Market': 25, 'SMB': 15 };
  score += sizeScores[account.size!] || 15;
  
  // Revenue scoring (0-25 based on revenue)
  const revScore = Math.min(25, Math.floor((account.annualRevenue || 0) / 10000000) * 5);
  score += revScore;
  
  // Region scoring
  const regionScores: Record<Region, number> = { 'North America': 20, 'Europe': 18, 'APAC': 15, 'LATAM': 12 };
  score += regionScores[account.region!] || 10;
  
  // Determine tier
  let tier: ICPTier;
  if (score >= 80) tier = 'A';
  else if (score >= 60) tier = 'B';
  else if (score >= 40) tier = 'C';
  else tier = 'D';
  
  return { score, tier };
}

// Generate accounts
export function generateAccounts(count: number = 300): Account[] {
  const industries: Industry[] = ['Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Education', 'Services'];
  const sizes: CompanySize[] = ['SMB', 'Mid-Market', 'Enterprise'];
  const regions: Region[] = ['North America', 'Europe', 'APAC', 'LATAM'];
  
  const accounts: Account[] = [];
  
  for (let i = 0; i < count; i++) {
    const size = randomItem(sizes);
    const employeeRanges: Record<CompanySize, [number, number]> = {
      'SMB': [10, 100], 'Mid-Market': [100, 1000], 'Enterprise': [1000, 50000]
    };
    const revenueRanges: Record<CompanySize, [number, number]> = {
      'SMB': [500000, 10000000], 'Mid-Market': [10000000, 100000000], 'Enterprise': [100000000, 5000000000]
    };
    
    const partialAccount = {
      industry: randomItem(industries),
      size,
      region: randomItem(regions),
      annualRevenue: randomInt(...revenueRanges[size]),
    };
    
    const { score, tier } = calculateICP(partialAccount as Account);
    
    accounts.push({
      id: `acc-${randomId()}`,
      name: generateCompanyName(),
      ...partialAccount,
      employees: randomInt(...employeeRanges[size]),
      website: `https://${generateCompanyName().toLowerCase().replace(' ', '')}.com`,
      icpScore: score,
      icpTier: tier,
    });
  }
  
  return accounts;
}

// Generate contacts for accounts
export function generateContacts(accounts: Account[]): Contact[] {
  const titles = ['CEO', 'CTO', 'CFO', 'VP Sales', 'VP Marketing', 'Director of Operations', 'Head of Product', 'Manager', 'Senior Engineer', 'Analyst'];
  const contacts: Contact[] = [];
  
  for (const account of accounts) {
    const contactCount = randomInt(1, 4);
    for (let i = 0; i < contactCount; i++) {
      const name = generateName();
      const [firstName, lastName] = name.split(' ');
      const domain = account.website.replace('https://', '').replace('http://', '');
      
      contacts.push({
        id: `contact-${randomId()}`,
        accountId: account.id,
        firstName,
        lastName,
        email: generateEmail(name, domain),
        phone: `+1-${randomInt(200, 999)}-${randomInt(100, 999)}-${randomInt(1000, 9999)}`,
        title: randomItem(titles),
        isPrimary: i === 0,
      });
    }
  }
  
  return contacts;
}

// Generate leads
export function generateLeads(
  accounts: Account[], 
  contacts: Contact[], 
  reps: SalesRep[], 
  count: number = 1000
): Lead[] {
  const sources: LeadSource[] = ['Organic', 'Paid', 'Events', 'Referral', 'Outbound'];
  const statuses: LeadStatus[] = ['New', 'Contacted', 'Qualified', 'Unqualified', 'Converted'];
  const statusWeights = [0.15, 0.25, 0.30, 0.10, 0.20]; // Distribution
  
  const leads: Lead[] = [];
  const now = new Date();
  const sixMonthsAgo = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
  
  for (let i = 0; i < count; i++) {
    const account = randomItem(accounts);
    const accountContacts = contacts.filter(c => c.accountId === account.id);
    const contact = accountContacts.length > 0 ? randomItem(accountContacts) : contacts[0];
    const createdAt = randomDate(sixMonthsAgo, now);
    
    // Weighted status selection
    const rand = Math.random();
    let cumulative = 0;
    let status: LeadStatus = 'New';
    for (let j = 0; j < statuses.length; j++) {
      cumulative += statusWeights[j];
      if (rand <= cumulative) {
        status = statuses[j];
        break;
      }
    }
    
    // Score based on ICP and recency
    const recencyBonus = Math.max(0, 30 - Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24)));
    const score = Math.min(100, account.icpScore + recencyBonus + randomInt(-10, 10));
    
    const lastActivityAt = randomDate(createdAt, now);
    const needsFollowUp = status !== 'Converted' && status !== 'Unqualified';
    
    leads.push({
      id: `lead-${randomId()}`,
      accountId: account.id,
      contactId: contact.id,
      ownerId: randomItem(reps).id,
      source: randomItem(sources),
      status,
      score,
      createdAt,
      lastActivityAt,
      nextFollowUp: needsFollowUp && Math.random() > 0.3 
        ? new Date(lastActivityAt.getTime() + randomInt(1, 14) * 24 * 60 * 60 * 1000)
        : undefined,
      account,
      contact,
    });
  }
  
  return leads;
}

// Generate opportunities
export function generateOpportunities(
  accounts: Account[],
  contacts: Contact[],
  reps: SalesRep[],
  products: Product[],
  leads: Lead[],
  count: number = 200
): Opportunity[] {
  const stages: DealStage[] = ['Discovery', 'Qualification', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
  const stageProbabilities: Record<DealStage, number> = {
    'Discovery': 10, 'Qualification': 25, 'Proposal': 50, 'Negotiation': 75, 'Closed Won': 100, 'Closed Lost': 0
  };
  
  const opportunities: Opportunity[] = [];
  const now = new Date();
  const sixMonthsAgo = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
  
  // Use converted leads as base for opportunities
  const convertedLeads = leads.filter(l => l.status === 'Converted');
  
  for (let i = 0; i < count; i++) {
    const lead = i < convertedLeads.length ? convertedLeads[i] : undefined;
    const account = lead?.account || randomItem(accounts);
    const accountContacts = contacts.filter(c => c.accountId === account.id);
    const contact = lead?.contact || (accountContacts.length > 0 ? randomItem(accountContacts) : contacts[0]);
    const rep = reps.find(r => r.id === lead?.ownerId) || randomItem(reps);
    const product = randomItem(products.filter(p => p.tier !== 'Custom'));
    
    // Determine stage with realistic distribution
    const stageRand = Math.random();
    let stage: DealStage;
    if (stageRand < 0.15) stage = 'Discovery';
    else if (stageRand < 0.30) stage = 'Qualification';
    else if (stageRand < 0.50) stage = 'Proposal';
    else if (stageRand < 0.65) stage = 'Negotiation';
    else if (stageRand < 0.85) stage = 'Closed Won';
    else stage = 'Closed Lost';
    
    const isAnnual = Math.random() > 0.3;
    const basePrice = isAnnual ? product.annualPrice : product.monthlyPrice * 12;
    
    // Adjust amount based on account size
    const sizeMultipliers: Record<CompanySize, number> = { 'SMB': 1, 'Mid-Market': 3, 'Enterprise': 10 };
    const amount = Math.round(basePrice * sizeMultipliers[account.size] * randomFloat(0.8, 1.5));
    
    const createdAt = lead ? new Date(lead.createdAt.getTime() + randomInt(1, 30) * 24 * 60 * 60 * 1000) : randomDate(sixMonthsAgo, now);
    
    // Generate stage history
    const stageHistory: StageHistoryEntry[] = [];
    const stageIndex = stages.indexOf(stage);
    let historyDate = new Date(createdAt);
    
    for (let j = 0; j <= stageIndex; j++) {
      const enteredAt = new Date(historyDate);
      const exitedAt = j < stageIndex ? new Date(historyDate.getTime() + randomInt(3, 21) * 24 * 60 * 60 * 1000) : undefined;
      stageHistory.push({ stage: stages[j], enteredAt, exitedAt });
      if (exitedAt) historyDate = exitedAt;
    }
    
    const lastStageEntry = stageHistory[stageHistory.length - 1];
    const daysInStage = Math.floor((now.getTime() - lastStageEntry.enteredAt.getTime()) / (1000 * 60 * 60 * 24));
    const daysOpen = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
    
    opportunities.push({
      id: `opp-${randomId()}`,
      accountId: account.id,
      contactId: contact.id,
      ownerId: rep.id,
      leadId: lead?.id,
      name: `${account.name} - ${product.name}`,
      stage,
      amount,
      productId: product.id,
      isAnnual,
      probability: stageProbabilities[stage],
      closeDate: new Date(createdAt.getTime() + randomInt(30, 120) * 24 * 60 * 60 * 1000),
      createdAt,
      lastActivityAt: randomDate(createdAt, now),
      stageHistory,
      nextStep: stage !== 'Closed Won' && stage !== 'Closed Lost' 
        ? randomItem(['Schedule demo', 'Send proposal', 'Executive meeting', 'Contract review', 'Final negotiation'])
        : undefined,
      account,
      contact,
      product,
      daysInStage,
      daysOpen,
    });
  }
  
  return opportunities;
}

// Generate activities
export function generateActivities(
  leads: Lead[],
  opportunities: Opportunity[],
  reps: SalesRep[],
  count: number = 2000
): Activity[] {
  const types: Activity['type'][] = ['call', 'email', 'meeting', 'note', 'task'];
  const subjects: Record<Activity['type'], string[]> = {
    call: ['Discovery call', 'Follow-up call', 'Demo call', 'Check-in call', 'Qualification call'],
    email: ['Introduction email', 'Follow-up email', 'Proposal sent', 'Thank you email', 'Meeting confirmation'],
    meeting: ['Initial meeting', 'Product demo', 'Executive briefing', 'Contract review', 'Kickoff meeting'],
    note: ['Meeting notes', 'Call summary', 'Customer feedback', 'Competitive intel', 'Requirements notes'],
    task: ['Send proposal', 'Schedule demo', 'Update CRM', 'Prepare presentation', 'Send contract'],
  };
  
  const activities: Activity[] = [];
  const now = new Date();
  const sixMonthsAgo = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
  
  for (let i = 0; i < count; i++) {
    const type = randomItem(types);
    const isOpportunity = Math.random() > 0.4;
    const createdAt = randomDate(sixMonthsAgo, now);
    
    activities.push({
      id: `act-${randomId()}`,
      type,
      subject: randomItem(subjects[type]),
      leadId: !isOpportunity ? randomItem(leads).id : undefined,
      opportunityId: isOpportunity ? randomItem(opportunities).id : undefined,
      ownerId: randomItem(reps).id,
      createdAt,
      completedAt: Math.random() > 0.2 ? randomDate(createdAt, now) : undefined,
      dueDate: type === 'task' ? new Date(createdAt.getTime() + randomInt(1, 14) * 24 * 60 * 60 * 1000) : undefined,
    });
  }
  
  return activities;
}
